/**
 * Standalone demo for Face Validator SDK (production build for Vercel).
 * This version includes the SDK bundled.
 */
import { FaceValidator, ValidationStatus } from '../src/index';
const CAMERA_CONTAINER_ID = 'cameraContainer';
const STATUS_ID = 'status';
const STATUS_CONTAINER_ID = 'statusContainer';
const THUMBNAILS_LIST_ID = 'thumbnailsList';
const LOCALE_ID = 'locale';
const DEBUG_ID = 'debugMode';
const BTN_RETRY_ID = 'btnRetry';
const STORAGE_KEY = 'face-validator-captures';
const MAX_THUMBNAILS = 3;
const AUTO_RESTART_DELAY_MS = 1200;
let validator = null;
let currentLocale = 'pt-BR';
let capturedImages = [];
let autoRestartTimeout = null;
// Traduções da interface
const translations = {
    'pt-BR': {
        title: '🎭 Face Validator SDK',
        subtitle: 'Validação de selfie em tempo real com detecção de face',
        languageLabel: '🌐 Idioma',
        debugLabel: '🔍 Modo Debug',
        debugCheckbox: 'Mostrar landmarks',
        retryButton: '🔄 Tentar Novamente',
        allowCameraButton: '📷 Permitir Acesso à Câmera',
        previewTitle: '✅ Captura realizada!',
        footerText: 'Desenvolvido com ❤️ usando',
        githubLink: 'Ver no GitHub',
        initialMessage: 'Clique no botão abaixo para permitir o acesso à câmera e iniciar a validação facial.',
        requestingCamera: 'Solicitando acesso à câmera...',
        cameraReady: 'Câmera pronta! Aguarde, iniciando validação...',
        cameraError: 'Erro ao acessar câmera',
        permissionDenied: 'Permissão negada. Por favor, permita o acesso à câmera nas configurações do navegador e recarregue a página.',
        cameraNotAvailable: 'Câmera não disponível. Recarregue a página.',
        startingValidation: 'Iniciando validação facial...',
        captureSuccess: 'Captura realizada com sucesso!',
        validationStopped: 'Validação parada. Clique em "Tentar Novamente" para recomeçar.',
    },
    'en': {
        title: '🎭 Face Validator SDK',
        subtitle: 'Real-time selfie validation with face detection',
        languageLabel: '🌐 Language',
        debugLabel: '🔍 Debug Mode',
        debugCheckbox: 'Show landmarks',
        retryButton: '🔄 Try Again',
        allowCameraButton: '📷 Allow Camera Access',
        previewTitle: '✅ Capture successful!',
        footerText: 'Developed with ❤️ using',
        githubLink: 'View on GitHub',
        initialMessage: 'Click the button below to allow camera access and start face validation.',
        requestingCamera: 'Requesting camera access...',
        cameraReady: 'Camera ready! Please wait, starting validation...',
        cameraError: 'Error accessing camera',
        permissionDenied: 'Permission denied. Please allow camera access in your browser settings and reload the page.',
        cameraNotAvailable: 'Camera not available. Reload the page.',
        startingValidation: 'Starting face validation...',
        captureSuccess: 'Capture successful!',
        validationStopped: 'Validation stopped. Click "Try Again" to restart.',
    },
    'es': {
        title: '🎭 Face Validator SDK',
        subtitle: 'Validación de selfie en tiempo real con detección de rostro y manos',
        languageLabel: '🌐 Idioma',
        debugLabel: '🔍 Modo Debug',
        debugCheckbox: 'Mostrar landmarks',
        retryButton: '🔄 Intentar Nuevamente',
        allowCameraButton: '📷 Permitir Acceso a la Cámara',
        previewTitle: '✅ ¡Captura realizada!',
        footerText: 'Desarrollado con ❤️ usando',
        githubLink: 'Ver en GitHub',
        initialMessage: 'Haga clic en el botón a continuación para permitir el acceso a la cámara e iniciar la validación facial.',
        requestingCamera: 'Solicitando acceso a la cámara...',
        cameraReady: 'Cámara lista! Espere, iniciando validación...',
        cameraError: 'Error al acceder a la cámara',
        permissionDenied: 'Permiso denegado. Por favor, permita el acceso a la cámara en la configuración del navegador y recargue la página.',
        cameraNotAvailable: 'Cámara no disponible. Recargue la página.',
        startingValidation: 'Iniciando validación facial...',
        captureSuccess: '¡Captura exitosa!',
        validationStopped: 'Validación detenida. Haga clic en "Intentar Nuevamente" para reiniciar.',
    },
};
function getEl(id) {
    const el = document.getElementById(id);
    if (!el)
        throw new Error(`Element #${id} not found`);
    return el;
}
// Gerenciamento de thumbnails no localStorage
function clearStoredThumbnails() {
    localStorage.removeItem(STORAGE_KEY);
    capturedImages = [];
}
function loadStoredThumbnails() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    }
    catch (error) {
        console.warn('Error loading thumbnails from localStorage:', error);
        return [];
    }
}
function saveThumbnail(imageDataUrl) {
    // Adicionar nova imagem no início
    capturedImages.unshift(imageDataUrl);
    // Manter apenas as últimas 3
    if (capturedImages.length > MAX_THUMBNAILS) {
        capturedImages = capturedImages.slice(0, MAX_THUMBNAILS);
    }
    // Salvar no localStorage
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(capturedImages));
    }
    catch (error) {
        console.warn('Error saving to localStorage:', error);
    }
    renderThumbnails();
}
function removeThumbnail(index) {
    // Remover do array
    capturedImages.splice(index, 1);
    // Atualizar localStorage
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(capturedImages));
    }
    catch (error) {
        console.warn('Error updating localStorage:', error);
    }
    // Re-renderizar
    renderThumbnails();
}
function toggleThumbnailZoom(thumbnailElement) {
    const overlay = document.getElementById('thumbnailOverlay');
    const isZoomed = thumbnailElement.classList.contains('zoomed');
    // Remover zoom de todos os thumbnails
    document.querySelectorAll('.thumbnail-item.zoomed').forEach(el => {
        el.classList.remove('zoomed');
    });
    if (isZoomed) {
        // Estava ampliado, fechar
        if (overlay)
            overlay.classList.remove('active');
    }
    else {
        // Ampliar este thumbnail
        thumbnailElement.classList.add('zoomed');
        if (overlay)
            overlay.classList.add('active');
    }
}
function renderThumbnails() {
    const container = document.getElementById(THUMBNAILS_LIST_ID);
    if (!container)
        return;
    container.innerHTML = '';
    capturedImages.forEach((imageDataUrl, index) => {
        const thumbnailItem = document.createElement('div');
        thumbnailItem.className = 'thumbnail-item fade-in';
        const img = document.createElement('img');
        img.src = imageDataUrl;
        img.alt = `Capture ${index + 1}`;
        // Clique no thumb: ampliar. Fechar zoom: clicar fora (overlay) ou tecla ESC.
        thumbnailItem.onclick = (e) => {
            if (thumbnailItem.classList.contains('zoomed'))
                return;
            toggleThumbnailZoom(thumbnailItem);
        };
        thumbnailItem.appendChild(img);
        container.appendChild(thumbnailItem);
    });
    // Fechar zoom ao clicar no overlay (fora da imagem)
    const overlay = document.getElementById('thumbnailOverlay');
    if (overlay) {
        overlay.onclick = () => closeThumbnailZoom();
    }
}
function closeThumbnailZoom() {
    document.querySelectorAll('.thumbnail-item.zoomed').forEach(el => {
        el.classList.remove('zoomed');
    });
    const overlay = document.getElementById('thumbnailOverlay');
    if (overlay)
        overlay.classList.remove('active');
}
function translate(key) {
    return translations[currentLocale][key];
}
function updatePageTexts() {
    const title = document.getElementById('pageTitle');
    const subtitle = document.getElementById('pageSubtitle');
    const languageLabel = document.getElementById('languageLabel');
    const debugLabel = document.getElementById('debugLabel');
    const debugCheckboxLabel = document.getElementById('debugCheckboxLabel');
    const retryButton = document.getElementById(BTN_RETRY_ID);
    const allowCameraButton = document.getElementById('btnAllowCamera');
    const initialMessage = document.getElementById('initialMessage');
    const previewTitle = document.getElementById('previewTitle');
    const footerText = document.getElementById('footerText');
    const githubLink = document.getElementById('githubLink');
    if (title)
        title.textContent = translate('title');
    if (subtitle)
        subtitle.textContent = translate('subtitle');
    if (languageLabel)
        languageLabel.textContent = translate('languageLabel');
    if (debugLabel)
        debugLabel.textContent = translate('debugLabel');
    if (debugCheckboxLabel)
        debugCheckboxLabel.textContent = translate('debugCheckbox');
    if (retryButton)
        retryButton.textContent = translate('retryButton');
    if (allowCameraButton)
        allowCameraButton.textContent = translate('allowCameraButton');
    if (initialMessage)
        initialMessage.textContent = translate('initialMessage');
    if (previewTitle)
        previewTitle.textContent = translate('previewTitle');
    if (footerText)
        footerText.textContent = translate('footerText');
    if (githubLink)
        githubLink.textContent = translate('githubLink');
}
function updateStatusUI(status, message) {
    const statusEl = getEl(STATUS_ID);
    const containerEl = getEl(STATUS_CONTAINER_ID);
    statusEl.textContent = message;
    // Remove classes anteriores
    containerEl.classList.remove('success', 'error', 'warning');
    // Adiciona classe baseada no status
    if (status === ValidationStatus.SUCCESS || status === ValidationStatus.CAPTURING) {
        containerEl.classList.add('success');
    }
    else if (status === ValidationStatus.ERROR) {
        containerEl.classList.add('error');
    }
    else if (status === ValidationStatus.OFF_CENTER ||
        status === ValidationStatus.TOO_CLOSE ||
        status === ValidationStatus.TOO_FAR ||
        status === ValidationStatus.POOR_ILLUMINATION ||
        status === ValidationStatus.FACE_OBSTRUCTED ||
        status === ValidationStatus.HEAD_NOT_STRAIGHT ||
        status === ValidationStatus.MULTIPLE_FACES) {
        containerEl.classList.add('warning');
    }
}
function destroyValidator() {
    if (validator) {
        validator.destroy();
        validator = null;
    }
}
function clearAutoRestart() {
    if (autoRestartTimeout) {
        clearTimeout(autoRestartTimeout);
        autoRestartTimeout = null;
    }
}
/**
 * Inicia a validação facial
 */
function startValidation() {
    const debugCheckbox = getEl(DEBUG_ID);
    const btnRetry = getEl(BTN_RETRY_ID);
    const allowCameraButton = document.getElementById('btnAllowCamera');
    const initialScreen = document.getElementById('initialScreen');
    clearAutoRestart();
    destroyValidator();
    // Ocultar tela inicial e botão de permitir
    if (initialScreen)
        initialScreen.style.display = 'none';
    if (allowCameraButton)
        allowCameraButton.style.display = 'none';
    // Ocultar botão Retry durante validação
    btnRetry.style.display = 'none';
    updateStatusUI(ValidationStatus.INITIALIZING, translate('requestingCamera'));
    // FaceValidator inicia automaticamente no construtor
    try {
        validator = new FaceValidator({
            container: `#${CAMERA_CONTAINER_ID}`,
            ui: 'none',
            locale: currentLocale,
            debugMode: debugCheckbox.checked,
            onStatusUpdate: (status, message) => {
                updateStatusUI(status, message);
            },
            onCaptureSuccess: (blob) => {
                updateStatusUI(ValidationStatus.SUCCESS, translate('captureSuccess'));
                // Converter blob para Data URL para salvar no localStorage
                const reader = new FileReader();
                reader.onloadend = () => {
                    const dataUrl = reader.result;
                    saveThumbnail(dataUrl);
                };
                reader.readAsDataURL(blob);
                // Reiniciar captura automaticamente apos um breve delay
                btnRetry.style.display = 'none';
                autoRestartTimeout = setTimeout(() => {
                    startValidation();
                }, AUTO_RESTART_DELAY_MS);
            },
            onError: (errorType, error) => {
                clearAutoRestart();
                updateStatusUI(errorType, `❌ Erro: ${error.message}`);
                console.error(errorType, error);
                // Mostrar botão retry em caso de erro
                btnRetry.style.display = 'block';
            },
        });
    }
    catch (err) {
        const error = err;
        updateStatusUI(ValidationStatus.ERROR, `Erro ao inicializar: ${error.message}`);
        // Mostrar botão retry em caso de erro
        btnRetry.style.display = 'block';
    }
}
/**
 * Reinicia a validação facial (para a validação atual e inicia uma nova)
 */
function retry() {
    clearAutoRestart();
    destroyValidator();
    updateStatusUI(ValidationStatus.INITIALIZING, translate('validationStopped'));
    // Reiniciar validação
    startValidation();
}
/**
 * Atualiza o idioma da aplicação
 */
function changeLanguage() {
    const localeSelect = getEl(LOCALE_ID);
    currentLocale = localeSelect.value;
    updatePageTexts();
    // Se há um validador ativo, reiniciar com novo idioma
    if (validator) {
        retry();
    }
}
/**
 * Inicializa a aplicação
 */
function init() {
    const btnRetry = document.getElementById(BTN_RETRY_ID);
    const btnAllowCamera = document.getElementById('btnAllowCamera');
    const localeSelect = getEl(LOCALE_ID);
    const initialScreen = document.getElementById('initialScreen');
    // Limpar capturas do localStorage ao carregar a página
    clearStoredThumbnails();
    renderThumbnails();
    // Ocultar botão Retry inicialmente
    if (btnRetry)
        btnRetry.style.display = 'none';
    // Mostrar tela inicial
    if (initialScreen)
        initialScreen.style.display = 'flex';
    // Event listeners
    if (btnRetry)
        btnRetry.addEventListener('click', retry);
    if (btnAllowCamera)
        btnAllowCamera.addEventListener('click', startValidation);
    localeSelect.addEventListener('change', changeLanguage);
    // Tecla ESC fecha o zoom do thumb
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape')
            closeThumbnailZoom();
    });
    console.log('Face Validator SDK Demo initialized');
    console.log('MediaPipe version: using CDN');
    // Configurar idioma inicial
    currentLocale = localeSelect.value;
    updatePageTexts();
}
// Inicializar quando o DOM estiver pronto
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
}
else {
    init();
}
